import { useEffect } from "react";
import { useNotificationStore } from "@/store";
import { subscribeToForegroundMessages } from "@/lib/firebaseMessaging";
import { toast } from "sonner";
import i18n from "@/lib/i18n.js";
import { mapNotificationRow } from "@/lib/notifications/map-notification-row.js";
import { resolveLocalizedNotificationCopy } from "@/lib/notifications/resolve-localized-notification-copy.js";

function sameOriginUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? url.href : '';
  } catch {
    return '';
  }
}

function parsePossiblyJson(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

export function useFcmForegroundNotifications() {
  const addNotification = useNotificationStore((s) => s.addNotification);

  useEffect(() => {
    const unsubscribe = subscribeToForegroundMessages((payload) => {
      const downloadUrl = sameOriginUrl(payload?.data?.downloadUrl);
      const rawData = payload?.data || {};
      const data = {
        ...rawData,
        i18n: parsePossiblyJson(rawData.i18n) || rawData.i18n,
      };

      const rawTitle = payload?.notification?.title ?? "Notification";
      const rawMessage = payload?.notification?.body ?? "";

      const notification = {
        id: payload?.data?.id ?? String(Date.now()),
        title: rawTitle,
        message: rawMessage,
        createdAt: payload?.data?.createdAt ?? new Date().toISOString(),
        isRead: false,
        type: payload?.data?.type ?? "info",
        actionUrl: downloadUrl,
        data,
        params: {
          ...(data.i18n?.params || {}),
          i18n: data.i18n,
          pageKey: data.pageKey,
          entityId: data.entityId,
          category: data.category,
          navigationKind: data.navigationKind,
        },
      };

      addNotification(notification);

      const mapped = mapNotificationRow({
        ...notification,
        _id: notification.id,
        body: notification.message,
        read: notification.isRead,
      });

      void (async () => {
        try {
          await i18n.loadNamespaces("notificationEvents");
        } catch {
          // Keep stored delivery strings if catalog cannot load.
        }
        const { title, message } = resolveLocalizedNotificationCopy(mapped, {
          title: mapped.title,
          message: mapped.message,
          language: i18n.resolvedLanguage || i18n.language,
        });

        toast.info(title, {
          description: message,
          ...(downloadUrl
            ? {
                action: {
                  label: i18n.t('detailedReporting.openSecureReport', {
                    ns: 'reportSurface',
                    defaultValue: 'Open report',
                  }),
                  onClick: () => window.location.assign(downloadUrl),
                },
              }
            : {}),
        });
      })();
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [addNotification]);
}
